/*
 * Nordic Tea (17/9): produktbilledet skal gaa i eet med sit kort.
 *
 * Billeder der er hoejere end pladsen (loftet i nt-custom.css) eller har et
 * andet format end galleriets hoejeste billede, vises smallere og centreret.
 * Luften ved siden af fik sidens cremefarve, og fotoet stod som en kasse i
 * kassen (Nicolai).
 *
 * Luften males med en blid overgang i fotoets egne kantfarver, aflaest langs
 * hver side: lysere foroven og moerkere forneden, hvis fotoet er det. At
 * traekke selve kantpixlerne ud gav striber (Nicolai 17/9), saa kun farverne
 * bruges. Er kanten urolig (et produkt der roerer kanten), faar luften kantens
 * mediane farve. Kan intet laeses, er luften hvid.
 * Fotos med en tydelig baggrund boer i stedet have baggrunden forlaenget i selve
 * billedfilen, som julekalenderen fik 17/9.
 */
(() => {
  if (window.ntBilledkant) return;
  window.ntBilledkant = true;

  const HVID = 'rgb(255, 255, 255)';
  // En gulvlinje i et studiefoto giver spring paa 15-30. Et moerkt produkt paa hvid
  // bund giver over 100, og et farvet produkt har maettede farver.
  const MAX_SPRING = 45;
  const MAX_MAETNING = 0.22;
  const SIDER = ['venstre', 'hoejre', 'top', 'bund'];

  // Farveproever langs en kant: median og om kanten er rolig.
  function kantProeve(img, side) {
    const bredde = img.naturalWidth;
    const hoejde = img.naturalHeight;
    const felt = Math.max(1, Math.round(Math.min(bredde, hoejde) * 0.006));
    const canvas = document.createElement('canvas');
    canvas.width = 1;
    canvas.height = 1;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    const vaerdier = [];
    for (let i = 1; i <= 11; i += 1) {
      const t = i / 12;
      let x = 0;
      let y = 0;
      if (side === 'venstre') y = (hoejde - felt) * t;
      if (side === 'hoejre') { x = bredde - felt; y = (hoejde - felt) * t; }
      if (side === 'top') x = (bredde - felt) * t;
      if (side === 'bund') { x = (bredde - felt) * t; y = hoejde - felt; }
      ctx.clearRect(0, 0, 1, 1);
      ctx.drawImage(img, x, y, felt, felt, 0, 0, 1, 1);
      const d = ctx.getImageData(0, 0, 1, 1).data;
      if (d[3] < 230) return null;
      vaerdier.push([d[0], d[1], d[2]]);
    }
    const kanal = (k) => vaerdier.map((v) => v[k]).sort((a, b) => a - b);
    const median = [0, 1, 2].map((k) => kanal(k)[Math.floor(vaerdier.length / 2)]);
    // Spring maales mellem naboer: en jaevn gradient (lys foroven, moerk forneden)
    // eller en gulvlinje er rolig, mens et produkt ved kanten giver et stort spring.
    let spring = 0;
    for (let i = 1; i < vaerdier.length; i += 1) {
      for (let k = 0; k < 3; k += 1) spring = Math.max(spring, Math.abs(vaerdier[i][k] - vaerdier[i - 1][k]));
    }
    const maetning = Math.max(...vaerdier.map(([r, g, b]) => {
      const hoej = Math.max(r, g, b);
      return hoej === 0 ? 0 : (hoej - Math.min(r, g, b)) / hoej;
    }));
    // Overgangens farvestop: hver prove glattet med sine naboer (median af tre).
    const stop = vaerdier.map((_, i) => {
      const nabo = vaerdier.slice(Math.max(0, i - 1), i + 2);
      return [0, 1, 2].map((k) => nabo.map((v) => v[k]).sort((a, b) => a - b)[Math.floor(nabo.length / 2)]);
    });
    return { farve: `rgb(${median.join(', ')})`, stop, rolig: spring <= MAX_SPRING && maetning <= MAX_MAETNING };
  }

  function analyser(img) {
    const svar = {};
    try {
      for (const side of SIDER) svar[side] = kantProeve(img, side);
    } catch (e) {
      // Pixels kan ikke laeses (andet domaene). At traekke kanten ud kraever det ikke.
      for (const side of SIDER) svar[side] = { farve: HVID, rolig: true };
    }
    return svar;
  }

  // Hvor fotoet faktisk staar i kortet (object-fit: contain, centreret).
  function billedfelt(media, img) {
    const m = media.getBoundingClientRect();
    const b = img.getBoundingClientRect();
    const forhold = img.naturalWidth / img.naturalHeight;
    let w = b.width;
    let h = w / forhold;
    if (h > b.height) { h = b.height; w = h * forhold; }
    return { bredde: m.width, hoejde: m.height, x: b.left - m.left + (b.width - w) / 2, y: b.top - m.top + (b.height - h) / 2, w, h };
  }

  function tegn(media, img, kanter) {
    if (!img.naturalWidth || !img.naturalHeight) return;
    const f = billedfelt(media, img);
    const hoejreKant = f.x + f.w;
    const bundKant = f.y + f.h;
    const venstre = f.x > 0.5;
    const hoejre = f.bredde - hoejreKant > 0.5;
    const top = f.y > 0.5;
    const bund = f.hoejde - bundKant > 0.5;
    let canvas = media.querySelector(':scope > canvas.nt-billedkant');
    if (!venstre && !hoejre && !top && !bund) {
      if (canvas) canvas.remove();
      return;
    }
    if (!canvas) {
      canvas = document.createElement('canvas');
      canvas.className = 'nt-billedkant';
      canvas.setAttribute('aria-hidden', 'true');
      media.prepend(canvas);
    }
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = Math.round(f.bredde * dpr);
    canvas.height = Math.round(f.hoejde * dpr);
    const ctx = canvas.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    // Hver side: en blid overgang langs siden i kantens farver, eller kantens
    // mediane farve hvis kanten er urolig.
    const side = (navn, maal, lodret) => {
      const k = kanter[navn];
      if (k && k.rolig && k.stop && k.stop.length > 1) {
        const g = lodret
          ? ctx.createLinearGradient(0, f.y, 0, f.y + f.h)
          : ctx.createLinearGradient(f.x, 0, f.x + f.w, 0);
        k.stop.forEach((c, i) => g.addColorStop((i + 1) / (k.stop.length + 1), `rgb(${c.join(', ')})`));
        g.addColorStop(0, `rgb(${k.stop[0].join(', ')})`);
        g.addColorStop(1, `rgb(${k.stop[k.stop.length - 1].join(', ')})`);
        ctx.fillStyle = g;
      } else {
        ctx.fillStyle = (k && k.farve) || HVID;
      }
      ctx.fillRect(maal[0], maal[1], maal[2], maal[3]);
    };
    // Siderne dækker hele hoejden/bredden, saa hjoernerne kommer med.
    if (venstre) side('venstre', [0, 0, f.x + 1, f.hoejde], true);
    if (hoejre) side('hoejre', [hoejreKant - 1, 0, f.bredde - hoejreKant + 1, f.hoejde], true);
    if (top) side('top', [0, 0, f.bredde, f.y + 1], false);
    if (bund) side('bund', [0, bundKant - 1, f.bredde, f.hoejde - bundKant + 1], false);
  }

  function farvLaeg(img) {
    const media = img.closest('.media');
    if (!media || img.dataset.ntKant) return;
    img.dataset.ntKant = '1';
    let kanter = null;
    let ventende = false;
    const opdater = () => {
      if (!img.naturalWidth) return;
      kanter = analyser(img);
      const foerste = SIDER.map((sd) => kanter[sd]).find(Boolean);
      media.style.backgroundColor = (foerste && foerste.farve) || HVID;
      tegn(media, img, kanter);
    };
    const tegnIgen = () => {
      if (ventende || !kanter) return;
      ventende = true;
      requestAnimationFrame(() => { ventende = false; tegn(media, img, kanter); });
    };
    // Aflaeses ved hver indlaesning: foerst kan det vaere en lille pladsholder,
    // bagefter skifter browseren til den rigtige stoerrelse fra srcset.
    img.addEventListener('load', opdater);
    new ResizeObserver(tegnIgen).observe(media);
    if (img.complete && img.naturalWidth) opdater();
  }

  function init(rod = document) {
    rod.querySelectorAll('#product-media .media-viewer__item .media img').forEach(farvLaeg);
  }

  function start() {
    init();
    const galleri = document.getElementById('product-media');
    if (!galleri) return;
    new MutationObserver((aendringer) => {
      for (const a of aendringer) {
        a.addedNodes.forEach((n) => {
          if (n.nodeType !== 1) return;
          if (n.matches('.media-viewer__item .media img')) farvLaeg(n);
          else n.querySelectorAll?.('.media-viewer__item .media img').forEach(farvLaeg);
        });
      }
    }).observe(galleri, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
  document.addEventListener('shopify:section:load', (evt) => init(evt.target));
})();
